import React, {useEffect, useState} from 'react';
import {CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis} from 'recharts';
import {
    adminClientGraphAPI,
    adminFirstClientAPI,
    adminFirstPaymentAPI,
    adminPaymentGraphAPI
} from '../../../services/admin';
import {GetGraphResponseData} from '../../../types/admin';
import styles from './AdminGraphCard.module.css';

type GraphType = 'clients' | 'payments';

//어드민 통계 그래프 카드
const AdminGraphCard = () => {
    const [data, setData] = useState<GetGraphResponseData[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
    const [graphType, setGraphType] = useState<GraphType>('clients');
    const [firstYear, setFirstYear] = useState<number>(new Date().getFullYear());

    //첫 연도부터 현재 연도까지
    const years = Array.from(
        {length: new Date().getFullYear() - firstYear + 1},
        (_, i) => new Date().getFullYear() - i
    );

    //그래프 Y축
    const formatYAxis = (value: number) => {
        return `₩${value.toLocaleString()}`;
    };

    //그래프 툴팁
    const formatTooltip = (value: number, graphType: GraphType) => {
        if (graphType === 'payments') {
            //결제 통계
            return [`₩${value.toLocaleString()}`, '결제 총액'];
        }
        //클라이언트 통계
        return [`${value.toLocaleString()}명`, '클라이언트 수'];
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const first = await (graphType === 'clients'
                ? adminFirstClientAPI() //첫 클라이언트 가입 연도
                : adminFirstPaymentAPI()); //첫 결제 연도
            const response = await (graphType === 'clients'
                ? adminClientGraphAPI(selectedYear) //월별 클라이언트 통계
                : adminPaymentGraphAPI(selectedYear)); //월별 결제 통계

            //그래프 사용 형식으로 변환
            const filledData = Array.from({length: 12}, (_, i) => {
                const existingData = response.find(item => item.month === i + 1);
                return existingData || {month: i + 1, amount: 0};
            });
            setFirstYear(first);
            setData(filledData);
        } catch (err) {
            console.error('Error fetching graph data:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedYear, graphType]);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h3 className={styles.title}>
                    {/*그래프 제목*/}
                    {graphType === 'clients' ? '월별 클라이언트 수' : '월별 결제 총액'}
                </h3>
                <div className={styles.selectGroup}>
                    {/*그래프 타입 설정, 클라이언트/결제*/}
                    <select
                        className={styles.select}
                        value={graphType}
                        onChange={(e) => setGraphType(e.target.value as GraphType)}
                    >
                        <option value="clients">월별 클라이언트 수</option>
                        <option value="payments">월별 결제 총액</option>
                    </select>
                    {/*연도 설정*/}
                    <select
                        className={styles.select}
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                        {years.map((year) => (
                            <option key={year} value={year}>
                                {year}년
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            {loading ? (
                <div className={styles.loadingContainer}>
                    로딩 중...
                </div>
            ) : (
                <div className={styles.graphContainer}>
                    <ResponsiveContainer width="100%" height="100%">
                        {/*그래프*/}
                        <LineChart
                            data={data}
                            margin={{top: 5, right: 30, left: 20, bottom: 5}}
                        >
                            <CartesianGrid strokeDasharray="3 3"/>
                            <XAxis
                                dataKey="month"
                                tickFormatter={(month) => `${month}월`}
                            />
                            <YAxis
                                tickFormatter={graphType === 'payments' ? formatYAxis : undefined}
                            />
                            <Tooltip
                                formatter={(value: number) =>
                                    formatTooltip(value, graphType)
                                }
                            />
                            <Line
                                type="monotone"
                                dataKey="amount"
                                stroke="#2563eb"
                                strokeWidth={2}
                                dot={{strokeWidth: 2}}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default AdminGraphCard;